import { Component, Element, Event, EventEmitter, h, Host, Prop, State } from "@stencil/core";
import { EncType, FormMethod, recaptchaScores, SubmitEvent } from "./constants/form-types";
import { FormComponent } from "../../common/form-component";
import { BUTTONTYPE_SUBMIT } from "../button/constants/button-constants";
import { GOOGLESCORE05 } from "./constants/form-constants";
import { KeyIds } from "../../constants/keys";
import { buildFormData } from "../../utils/form-data-helper";
import { DefaultLocalization } from "../../common/localizable-component";
import { load, ReCaptchaInstance } from "recaptcha-v3";
import webComponentsDebug from "../../utils/log-helper";
import { detectSlotChanges } from "../../utils/slot-helper";
import { isDesktopOrLarger } from "../../utils/screen-helper";
import { scrollToElement } from "../../utils/scroll-helper";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - Holds any HTML content and expects any number of (nested) E.ON UI Form Elements. You can wrap these Form Elements if necessary.
 * @slot [dynamic] - Form uses a dynamic number of named slots to handle different HTTP request status codes. Use a unique slot name per status code! Each dynamic slot expects a single eon-ui-form-message element as child.
 */
@Component({
  tag: "eon-ui-form",
  styleUrl: "form.scss",
  shadow: true
})
export class Form {
  // @InitLogging() sentryTransaction;
  private formData: FormData;
  /**
   * The Awaited Google Recaptcha Object
   */
  private recaptcha: ReCaptchaInstance;

  @Element() hostElement: HTMLEonUiFormElement;

  /**
   * Raised when the form has been loaded.
   */
  @Event() formLoad: EventEmitter;

  /**
   * Event to be fired when the form will submit
   */
  @Event() formSubmit: EventEmitter<SubmitEvent>;

  @State() formElements: Array<FormComponent<any, DefaultLocalization>>;

  @State() requestStatus: string;
  @State() captchaIsValid: boolean = true;
  @State() captchaError: boolean = false;

  /**
   * Set the method of the form submit
   */
  @Prop() method: FormMethod;

  /**
   * The name Attribute is a string that refers to the native name attribute of an HtmlFormElement.
   * @example <eon-ui-form name="Name of this Form"/>
   */
  @Prop() name: string;

  /**
   * The Url of the recaptcha Validation Server that validates against the Recaptcha Token.
   */
  @Prop() recaptchaValidationUrl: string;

  /**
   * Takes the Recaptcha Public Key as a string<br>
   * If you use ReCaptcha, make sure you give the form a name too!<br>
   * the neme of the Form is used as divider for the recaptcha service
   */
  @Prop() recaptchaPublicKey: string;

  /**
   * The recaptchaScore is used to decide whether it is a human user or a bot.
   * You have to make the decision yourself, you only get a score back from Google
   * Recommendation is a anything that gets a score of =< 0.5 is a bot.
   * Available values are: 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0
   * https://www.google.com/recaptcha/about/
   */
  @Prop() recaptchaScore: recaptchaScores = GOOGLESCORE05;

  /**
   * The action URL to be called on submit
   */
  @Prop() action: string;

  /**
   * Use this for method="post". The enctype is the MIME type of the form submission.
   * Available values are:
   * undefined: Let the browser decide which is the best for your request: The default value.
   * application/x-www-form-urlencoded
   * multipart/form-data: Use this if the form contains "input" elements with type=file.
   * text/plain: Introduced by HTML5 for debugging purposes.
   */
  @Prop() enctype: EncType = undefined;

  /**
   * Indicates whether input elements can by default have their values automatically completed by the browser.
   * Provides automated assistance in filling out form field values, as well as guidance to the browser as to the type of information expected in the field.
   * Typically values come from past values entered by the user, but they may also come from pre-configured values which can be set in autofill feature provided by browser.
   * This Property gives liberty to input fields to participate in Form Autofill feature of broswer
   * To make Autocomplete and autofill work on input fields in Forms, "autocompleteKeyword" is property which should be assigned to eon-ui-input component
   * @example <eon-ui-form autocomplete="on" action="https://eon-ui-backend.azurewebsites.net/formsend" method="post">
   * <eon-ui-input label="First Name" autocomplete-keyword="given-name" id="fname" name="fname"></eon-ui-input>
   * </eon-ui-form>
   * Source: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  @Prop() autocomplete: "on" | "off";

  /**
   * Redirect to the given URL when the form is submitted. If an action is provided, the page will only be redirected when the action was successful.
   */
  @Prop() redirect: string;

  /**
   * Triggers form validation and processes the form data
   * @param e Event which fired the submit button
   */
  /* istanbul ignore next */
  private async onSubmitEvent(e: Event) {
    const isFormValid = await this.isFormValid();

    if (isFormValid) {
      this.formData = this.getFormData();

      if (this.action && this.method) {
        try {
          const response = await this.sendFormRequest();
          this.formSubmit.emit({ event: e, formData: this.formData, response });
          if (this.redirect) {
            window.location.replace(this.redirect);
          }
        } catch (error) {
          this.formSubmit.emit({ event: e, formData: this.formData, response: error });
        }
      } else {
        this.formSubmit.emit({ event: e, formData: this.formData });
        if (this.redirect) {
          window.location.replace(this.redirect);
        }
      }
    }
  }

  private getFormData(): FormData {
    return buildFormData(this.formElements);
  }

  /**
   * Sends the form request to the given action URL with the given encryption type
   */
  /* istanbul ignore next */
  private sendFormRequest(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let request = new XMLHttpRequest();
      request.open(this.method, this.action);
      if (this.enctype) {
        request.setRequestHeader("Content-Type", this.enctype);
      }
      request.onreadystatechange = () => {
        if (request.readyState == 4) {
          this.requestStatus = request.status.toString();
          if (request.status == 200) {
            //if status is successful, resolve the promise, else reject
            resolve(request.response);
          } else {
            reject(request.response);
          }
        }
      };
      request.send(this.formData);
    });
  }

  componentWillLoad() {
    this.getFormElements();
    this.appendSubmitEventToButton();
  }

  componentDidLoad() {
    this.hasProvidedRecaptchaPubKey();
    detectSlotChanges(
      () => {
        this.getFormElements();
      },
      this.hostElement,
      { childList: true, subtree: true }
    );

    this.formLoad.emit();
  }

  /* istanbul ignore next */
  private hasProvidedRecaptchaPubKey() {
    if (this.recaptchaPublicKey) {
      this.initRecaptcha();
    }
  }

  /* istanbul ignore next */
  private initRecaptcha() {
    load(this.recaptchaPublicKey).then((recaptcha) => {
      this.recaptcha = recaptcha;
    });
  }

  /* istanbul ignore next */
  private async executeRecaptcha() {
    let recaptchaActionWithoutSpace;
    if (!!this.name) {
      //replace space
      const recaptchaAction = this.name;
      recaptchaActionWithoutSpace = recaptchaAction.replace(/\s/g, "_") + "_submit";
    } else {
      recaptchaActionWithoutSpace = `Form_submit`;
    }
    return this.recaptcha.execute(recaptchaActionWithoutSpace);
  }

  /**
   * call the function to ask google server
   * Calculates whether the user is real or a bot based on the Google score returned
   * @param token
   * @private
   * @returns {boolean}
   */
  /* istanbul ignore next */
  private async validateRecaptchaToken(token: string) {
    // Send your token to the backendValidation Server and wait for response
    return await this.createValidationRequest(token, this.recaptchaValidationUrl).then((data) => {
      if (data) {
        let recaptchaScore = data.google_response.score;
        if (recaptchaScore <= this.recaptchaScore) {
          // Bad Score
          return false;
        } else {
          // Good Score
          return true;
        }
      }
    });
  }

  /**
   * Generates the connection to the captcha backend server.
   * The Google-ReCaptcha answer is expected, such as:
   *    google_response: {success: true, challenge_ts: "2021-01-22T11:15:50Z", hostname: "192.168.178.82", score: 0.9, action: }
   *    @return google_response: {success: true, challenge_ts: "2021-01-22T11:15:50Z", hostname: "192.168.178.82", score: 0.9, action: }
   * The MTCaptcha answer is expected as HTTP Header named "success":
   *    response.header.get("success")
   */
  /* istanbul ignore next */
  async createValidationRequest(token: string, captchaValidationUrl: string, method: string = "POST") {
    let data;
    try {
      const bodyJSON = { recaptcha_token: token };
      const response = await fetch(captchaValidationUrl, {
        method: method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: method == "POST" ? JSON.stringify(bodyJSON) : undefined
      });

      data = (await method) === "POST" ? response.json() : response;
    } catch (e) {
      // e.g.: Connection cannot be established
      if (webComponentsDebug) console.log("Connection refused:", e);
      this.captchaError = true;
      data = false;
    }
    return data;
  }

  /**
   * Check if an object is a type of FormComponent.
   * Basically checks if the object has a property named isFormComponent
   * @param object The object to check
   */
  private isFormComponent(object: any): object is FormComponent<object, DefaultLocalization> {
    return "isFormComponent" in object;
  }

  /**
   * Triggers validation on every Form Component and checks if the components are valid
   */
  /* istanbul ignore next */
  private async isFormValid(): Promise<boolean> {
    let isValid = true;
    if (this.getMtCaptchaElement()) {
      const token = this.getMtCaptchaToken();

      // If mtcaptcha input is visible token is empty if user did not enter anything into this input.
      // Still the form validation should be triggered in that case.
      if (!token) {
        this.validateFormElements();
        return false;
      }

      let mtCaptchaValidationUrl = this.hostElement.querySelector("#mtchaptcha").getAttribute("data-url");
      const resourcePage = this.hostElement.querySelector("eon-ui-mt-captcha").getAttribute("resource-page");

      if (mtCaptchaValidationUrl) {
        mtCaptchaValidationUrl = `${mtCaptchaValidationUrl}?token=${token}&resource-page=${encodeURIComponent(
          resourcePage
        )}`;

        isValid = await this.createValidationRequest(token, mtCaptchaValidationUrl, "GET").then(async (data) => {
          if (data && data.headers.get("success")) {
            // Good Result
            return await this.validateFormElements();
          } else {
            await this.validateFormElements();
          }
          // Bad Result
          return false;
        });
      } else {
        if (webComponentsDebug) console.log("Captcha validation URL isn't set");
        isValid = false;
      }
    } else if (this.recaptchaPublicKey) {
      // Async Recaptcha Process if PublicKey is given.
      const token = await this.executeRecaptcha();
      const captchaIsValid = await this.validateRecaptchaToken(token);

      if (captchaIsValid) {
        isValid = await this.validateFormElements();
        this.captchaIsValid = true;
      } else {
        isValid = false;
        if (!this.captchaError) {
          this.captchaIsValid = false;
        }
      }
    } // if no RecaptchaPublic Key is given there will be no async check necessary.
    else {
      isValid = await this.validateFormElements(); //Wenn Fehler === true
    }
    return isValid;
  }

  private getMtCaptchaElement() {
    return this.hostElement?.querySelector("eon-ui-mt-captcha");
  }

  private getMtCaptchaToken() {
    const mtCaptchaTokenElement = this.hostElement?.querySelector('input[name="mtcaptcha-verifiedtoken"]');
    return (mtCaptchaTokenElement as HTMLInputElement).value;
  }

  /**
   * If the Form has a button of type submit, the submit event is attached to the click function
   */
  private appendSubmitEventToButton() {
    const buttonElements = Array.from(this.hostElement.getElementsByTagName("eon-ui-button"));
    buttonElements.forEach((button) => {
      if (button?.inputType === BUTTONTYPE_SUBMIT) {
        button.onclick = (e) => {
          this.onSubmitEvent(e);
        };
      }
    });
  }

  private async validateFormElements() {
    let isValid = true;
    let firstErrorElm;
    let elmNode;

    for (let i = 0; i < this.formElements.length; i++) {
      const element = this.formElements[i];
      const isElementValid = await element.validate();
      elmNode = element;
      if (!isElementValid) {
        isValid = false;
        if (!firstErrorElm && elmNode.offsetParent) {
          firstErrorElm = element;
          this.scrollToError(firstErrorElm);
        }
      }
    }

    return isValid;
  }

  private scrollToError(elm) {
    let mainNavigation, navigationToolbar, metaNavigation;
    let websiteNavigation = document.querySelector("eon-ui-website-navigation");

    if (websiteNavigation) {
      mainNavigation = document.querySelector("eon-ui-website-navigation-main");
      navigationToolbar = document.querySelector("eon-ui-website-navigation-toolbar");
      metaNavigation = document.querySelector("eon-ui-website-navigation-meta");
    } else {
      mainNavigation = document.querySelector("eon-ui-navigation-main");
      navigationToolbar = document.querySelector("eon-ui-navigation-toolbar");
      metaNavigation = document.querySelector("eon-ui-navigation-meta");
    }
    const thirdLevelNavigation = document.querySelector("eon-ui-third-level-navigation");
    const margin = 48;
    let thirdLevelNavigationHeight = 0;

    if (thirdLevelNavigation) {
      if (isDesktopOrLarger()) {
        thirdLevelNavigationHeight = 68;
      } else {
        thirdLevelNavigationHeight = 48;
      }
    }

    const scrollOffset: number =
      (mainNavigation ? mainNavigation.getBoundingClientRect().height : 0) +
      (navigationToolbar ? navigationToolbar.getBoundingClientRect().height : 0) +
      (metaNavigation ? metaNavigation.getBoundingClientRect().height : 0) +
      thirdLevelNavigationHeight +
      margin;

    scrollToElement(elm, {
      offset: -scrollOffset
    });
  }

  private getFormElements() {
    const descendants = Array.from(this.hostElement.querySelectorAll("*"));

    const formElements = (descendants as any).filter((element) => {
      //can't directly cast element to FormComponent
      return this.isFormComponent(element);
    }) as Array<FormComponent<object, DefaultLocalization>>;
    this.formElements = formElements;
  }

  /**
   * Catch the ENTER key to submit the form
   * @param e The keyboard event
   */
  private async handleKeyDown(e: KeyboardEvent): Promise<void> {
    if (e.key === KeyIds.ENTER) {
      await this.onSubmitEvent(e);
    }
  }

  render() {
    return (
      <Host onkeydown={async (e) => await this.handleKeyDown(e)}>
        <form onSubmit={async (e) => await this.onSubmitEvent(e)} autocomplete={this.autocomplete}>
          <slot />
        </form>
        <slot name={this.requestStatus}></slot>
        {!this.captchaIsValid && <slot name="captchaMessage"></slot>}
        {this.captchaError && <slot name="captchaError"></slot>}
      </Host>
    );
  }
}
